import { useAuth } from '@/hooks/useAuth'
import { useEstimates } from '@/hooks/useEstimates'
import {BookmarkPlus, Check, Loader2} from 'lucide-react'
import { useState } from 'react'
import AuthModal from './AuthModal'

interface SaveEstimateButtonProps {
  calculatorType: 'tattoo' | 'laser'
  summary: string
  inputs: Record<string, unknown>
  results: Record<string, unknown>
  className?: string
}

function SaveEstimateButton({ calculatorType, summary, inputs, results, className }: SaveEstimateButtonProps) {
  const { user, isAuthenticated } = useAuth()
  const { saveEstimate } = useEstimates()
  const [authOpen, setAuthOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const handleSave = async () => {
    if (!isAuthenticated || !user?.userId) {
      setAuthOpen(true)
      return
    }
    setSaving(true)
    try {
      await saveEstimate({
        userId: user.userId,
        calculatorType,
        summary,
        inputs,
        results,
      })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    }
    catch {
      // error already logged in hook
    }
    finally {
      setSaving(false)
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className={
          className
          || 'inline-flex items-center justify-center gap-2 rounded-full border border-amber-400/40 bg-amber-500/10 px-5 py-2.5 text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-500/20 disabled:cursor-not-allowed disabled:opacity-60'
        }
      >
        {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {!saving && saved && <Check className="h-4 w-4" aria-hidden="true" />}
        {!saving && !saved && <BookmarkPlus className="h-4 w-4" aria-hidden="true" />}
        {saved ? 'Saved to My Estimates' : 'Save This Estimate'}
      </button>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </>
  )
}

export default SaveEstimateButton
